import type { AddEntityRequest } from '@/backend/addEntity'
import { useLimitedAction } from '@/modules/Billing/useLimitedAction'
import type { DataField, FileCollectionField, FileField } from '@/modules/Project/Fields/types'
import { promiseQueue } from '@/modules/Workspaces/KnowledgeHub/Files/promiseQueue'
import type { GoogleDriveFile } from '@/modules/Workspaces/KnowledgeHub/Integrations/GoogleDrive/useGoogleDriveConnection'
import { useIntegrationStore } from '@/modules/Workspaces/KnowledgeHub/useIntegrationStore'
import { useSetFieldValue } from '@/shared/useSetFieldValue'
import { invariant } from '@/shared/utils/typeAssertions'

type SharedProps = {
  files: GoogleDriveFile[]
  getExtraFields?: (file: GoogleDriveFile) => AddEntityRequest['fields']
  projectId: string
  workspaceId: string
}

type PropertyProps = SharedProps & {
  propertyId: string
  parentEntityId?: string
}

type FieldProps = SharedProps & {
  field: FileField | FileCollectionField | DataField
}

export async function uploadGoogleDriveURLs(props: PropertyProps | FieldProps) {
  const firstFile = props.files.at(0)
  invariant(firstFile)

  const integrationStore = useIntegrationStore()
  const driveConnection = integrationStore.findConnection('google_drive')
  invariant(driveConnection)

  const { addEntity } = useLimitedAction()
  const propertyId = 'propertyId' in props ? props.propertyId : props.field.propertyId
  const parentEntityId = 'parentEntityId' in props ? props.parentEntityId : undefined
  /**
   * List of promises that will create an entity for every given file
   */
  const allFilesCreationPromises = props.files.map((file) => () => {
    return addEntity({
      projectId: props.projectId,
      workspaceId: props.workspaceId,
      parentEntityId,
      fields: {
        ...(props.getExtraFields?.(file) || {}),
        [propertyId]: {
          connection_id: driveConnection.id,
          integration_id: driveConnection.integrationId,
          file_id: file.id,
          file_name: file.name,
        },
      },
    })
  })

  // If an entityId is provided, we don't want to create an entity for the first file
  // and instead we want to set the value on the existing entity
  if ('field' in props) {
    const { entityId, propertyId } = props.field

    allFilesCreationPromises[0] = () => {
      const setFieldValue = useSetFieldValue()
      const fields = {
        [propertyId]: {
          field: props.field,
          newValue: {
            connection_id: driveConnection.id,
            integration_id: driveConnection.integrationId,
            file_id: firstFile.id,
            file_name: firstFile.name,
          },
        },
      }
      return setFieldValue({
        workspaceId: props.workspaceId,
        projectId: props.projectId,
        entityId,
        fields,
      })
    }
  }

  await promiseQueue(allFilesCreationPromises, { maxConcurrent: 20 })
}
